import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Bell, BellOff, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nContext";
import { markPrompted, needsHomeScreen, newsRelang, newsSubscribe, newsUnsubscribe, shouldSoftPrompt, useNewsPush } from "@/lib/newsPush";

// « Être prévenu des nouveautés »: permission requested only after the click; « Ne plus me prévenir » toggles off.
export const NewsPushButton = ({ className = "", testId = "news-push-btn", onDone }) => {
  const { t, lang } = useI18n();
  const { on, supported, denied: blocked } = useNewsPush();
  const denied = blocked && !needsHomeScreen();
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState(false);
  if (!supported && !needsHomeScreen()) return null;
  const click = async () => {
    if (needsHomeScreen()) {
      setHint(true);
      return;
    }
    setBusy(true);
    try {
      if (on) await newsUnsubscribe();
      else await newsSubscribe(lang);
      toast.success(t(on ? "news.off" : "news.on"));
      onDone?.();
    } catch {
      toast.error(t(Notification.permission === "denied" ? "news.denied" : "news.error"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`min-w-0 ${className}`}>
      <button type="button" onClick={click} disabled={busy || (denied && !on)}
        className="inline-flex max-w-full items-center gap-2 rounded-full border border-[rgba(245,197,66,0.35)] px-3 py-1.5 text-start text-xs font-semibold text-[var(--gold-bright)] transition-colors hover:bg-[rgba(245,197,66,0.1)] disabled:opacity-60"
        data-testid={testId}>
        {busy ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" /> : on ? <BellOff className="h-3.5 w-3.5 shrink-0" /> : <Bell className="h-3.5 w-3.5 shrink-0" />}
        <span className="min-w-0 break-words">{t(on ? "news.stop" : "news.cta")}</span>
      </button>
      {(hint || (denied && !on)) && <p className="mt-1 max-w-sm break-words text-[11px] text-neutral-400" data-testid={`${testId}-hint`}>{t(hint ? "news.iosHint" : "news.deniedHint")}</p>}
    </div>
  );
};

export const NewsSoftPrompt = () => {
  const { t, lang } = useI18n();
  useEffect(() => {
    newsRelang(lang);
  }, [lang]);
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);
  const { on, supported, denied } = useNewsPush();
  useEffect(() => {
    if (!shouldSoftPrompt() || pathname.startsWith("/admin") || pathname.startsWith("/espace-")) return undefined;
    const id = setTimeout(() => setShow(true), 12000);
    return () => clearTimeout(id);
  }, [pathname]);
  if (!show || on || denied || (!supported && !needsHomeScreen())) return null;
  const close = () => {
    markPrompted();
    setShow(false);
  };
  return (
    <div className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-sm rounded-2xl border border-[rgba(245,197,66,0.3)] bg-[rgba(14,11,12,0.96)] p-4 shadow-2xl backdrop-blur sm:inset-x-auto sm:end-4" role="dialog" data-testid="news-soft-prompt">
      <button type="button" onClick={close} className="absolute end-2 top-2 rounded-full p-1 text-neutral-400 hover:text-white" aria-label={t("news.later")} data-testid="news-soft-close"><X className="h-4 w-4" /></button>
      <p className="pe-6 break-words text-sm text-white">{t("news.prompt")}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <NewsPushButton testId="news-soft-accept" onDone={close} />
        <button type="button" onClick={close} className="text-xs text-neutral-400 hover:text-white" data-testid="news-soft-later">{t("news.later")}</button>
      </div>
    </div>
  );
};
